import {
  useState,
  useCallback,
  useEffect,
  useContext,
  createContext,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { fmt, statusColor } from "./Utils/Utilities";
import { Badge } from "./SharedUI";
import { downloadOrderPdf } from "./Utils/pdf";
import { usePermissions } from "./PermissionsContext";
import "./index.css";
import "./Maintenance.css";

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = "https://api.seritonex.com";
const CDN_BASE = "https://cdn.seritonex.com";
const token = () => localStorage.getItem("token");

const decodeToken = () => {
  try {
    const jwt = token();
    if (!jwt) return null;
    const base64 = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

const organizationId = () => {
  const payload = decodeToken();
  return parseInt(payload?.organization?.organization_id ?? 1, 10);
};

const getUserScope = () => {
  const payload = decodeToken();
  if (!payload) return { type: "org" };
  if (payload.supplier_branch_id)
    return {
      type: "supplier_branch",
      id: parseInt(payload.supplier_branch_id, 10),
    };
  if (payload.branch_id)
    return { type: "branch", id: parseInt(payload.branch_id, 10) };
  if (payload.warehouse_id)
    return { type: "warehouse", id: parseInt(payload.warehouse_id, 10) };
  return { type: "org" };
};

const getLoggedInUserId = () => {
  const payload = decodeToken();
  return payload?.user_id ?? payload?.id ?? payload?.sub ?? null;
};

// ─── Role detection ───────────────────────────────────────────────────────────
const detectJwtRole = () => {
  const payload = decodeToken();
  if (!payload) return "customer";
  if (payload.supplier_branch_id) return "supplier";
  if (payload.branch_id || payload.warehouse_id) return "customer";
  if (payload.is_supplier === true || payload.is_supplier === 1)
    return "supplier";
  return "customer";
};

const RoleOverrideContext = createContext(null);
const useRole = () => {
  const ctx = useContext(RoleOverrideContext);
  if (ctx?.role) return ctx.role;
  return detectJwtRole();
};

// ─── HTTP helpers ─────────────────────────────────────────────────────────────
const apiFetch = async (path, body) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `${res.status}: ${res.statusText}`);
  }
  return res.json();
};

// ─── Purchase Order API ───────────────────────────────────────────────────────
const fetchOrders = () =>
  apiFetch("/api/v1/amis/purchase-orders/all", {
    organization_id: organizationId(),
    is_supplier: false,
  });

const getOrdersByBranch = async (branch_id) => {
  const raw = await apiFetch("/api/v1/amis/purchase-orders/by-branch", {
    branch_id: parseInt(branch_id, 10),
    is_supplier: false,
  });
  return Array.isArray(raw) ? raw : (raw?.purchase_orders ?? []);
};

const getOrdersByWarehouse = async (warehouse_id) => {
  const raw = await apiFetch("/api/v1/amis/purchase-orders/by-warehouse", {
    warehouse_id: parseInt(warehouse_id, 10),
  });
  return Array.isArray(raw) ? raw : (raw?.purchase_orders ?? []);
};

const getOrderDetail = async (purchase_order_id) => {
  const res = await fetch(`${API_BASE}/api/v1/amis/purchase-orders/by-id`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
    },
    body: JSON.stringify({ purchase_order_id }),
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to get order details (${res.status})`);
  return res.json();
};

const cancelOrder = (purchase_order_id) =>
  fetch(`${API_BASE}/api/v1/amis/purchase-orders/cancel`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
    },
    body: JSON.stringify({
      purchase_order_id: parseInt(purchase_order_id, 10),
    }),
  }).then((r) => {
    if (!r.ok) throw new Error(`Failed to cancel order: ${r.statusText}`);
    return r.json();
  });

// ─── Quotation API ────────────────────────────────────────────────────────────
const getQuoteByPurchaseOrder = async (purchase_order_id) => {
  const raw = await apiFetch("/api/v1/amis/quotations/by-purchase-order", {
    purchase_order_id,
  });
  return Array.isArray(raw) ? raw : (raw?.quotations ?? []);
};

const getQuoteById = (quotation_id) =>
  apiFetch("/api/v1/amis/quotations/by-id", { quotation_id });

const createQuote = (body) => apiFetch("/api/v1/amis/quotations/create", body);

const updateQuoteStatus = (quotation_id, status, notes = "") =>
  apiFetch("/api/v1/amis/quotations/update-status", {
    quotation_id,
    status,
    notes,
  });

// ─── Invoice API ──────────────────────────────────────────────────────────────
const getInvoicesByPurchaseOrder = (purchase_order_id) =>
  apiFetch("/api/v1/amis/invoice/by-purchase-order", { purchase_order_id });

const createInvoice = (body) => apiFetch("/api/v1/amis/invoice/create", body);

// ─── VAT rate helper ──────────────────────────────────────────────────────────
const normaliseVatRate = (raw) => {
  if (raw == null) return null;
  const n = parseFloat(raw);
  if (isNaN(n)) return null;
  return n <= 1 ? n * 100 : n;
};

// ─── CSV export helper ────────────────────────────────────────────────────────
const downloadCsv = (filename, rows, headers) => {
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const lines = [
    headers.map(escape).join(","),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── PDF download helper ──────────────────────────────────────────────────────
const downloadPdfFile = async (url, fallbackFilename) => {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token()}` },
  });
  if (!res.ok) throw new Error(`Failed to download PDF (${res.status})`);
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition");
  let filename = fallbackFilename;
  if (disposition) {
    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match?.[1])
      filename = match[1].replace(/['"]/g, "").trim() || fallbackFilename;
  }
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
};

// ─── Normalisers ──────────────────────────────────────────────────────────────
const normaliseOrder = (raw) => ({
  id: raw.purchase_order_number ?? String(raw.purchase_order_id),
  _id: parseInt(raw.purchase_order_id, 10),
  date: raw.order_date,
  delivery: raw.required_delivery_date,
  status: raw.status?.description ?? "Pending",
  // Raw numeric status id — drives the cancel-eligibility check below.
  // Description strings ("New", "Cancelled", etc.) aren't reliable for this
  // since they vary by wording; the id is the source of truth.
  statusId: raw.status?.purchase_order_status_id ?? null,
  site: raw.customer_branch?.branch_name ?? "—",
  supplier: raw.supplier_branch?.organization.name,
  notes: raw.notes,
  address: [
    raw.address_line_1,
    raw.address_line_2,
    raw.city,
    raw.region,
    raw.postal_code,
    raw.country,
  ]
    .filter(Boolean)
    .join(", "),
  _raw: raw,
});

// Formats an address object into a single display string, ordered as:
// city, region, address_line_1, address_line_2, postal_code, country.
// Used for the quote page's Warehouse / Customer Branch / Supplier Branch
// address fields (via normalisePoSnapshot below).
const formatAddress = (addr) => {
  if (!addr) return null;
  const parts = [
    addr.city,
    addr.region,
    addr.address_line_1,
    addr.address_line_2,
    addr.postal_code,
    addr.country,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
};

// Splits an address object into individual labeled rows (Address Line 1,
// Address Line 2, City, Region, Postal Code, Country) for display, instead
// of collapsing everything into a single mashed-together string. Rows with
// no value are filtered out by the callers, same as every other field.
const addressRows = (addr) => {
  if (!addr) return [];
  return [
    ["Address Line 1", addr.address_line_1],
    ["Address Line 2", addr.address_line_2],
    ["City", addr.city],
    ["Region", addr.region],
    ["Postal Code", addr.postal_code],
    ["Country", addr.country],
  ];
};

// The by-id quotation response nests a full snapshot of the parent purchase
// order (warehouse, customer/supplier branch, dates) under `purchase_order`.
// This is only present on the by-id call (never on the by-purchase-order
// list), so we capture it here for pages that only have the quote to work
// from (e.g. QuoteViewPage) rather than re-fetching the order separately.
const normalisePoSnapshot = (po) => {
  if (!po) return null;
  return {
    id: po.purchase_order_id,
    number: po.purchase_order_number,
    orderDate: po.order_date,
    requiredDeliveryDate: po.required_delivery_date,
    warehouse: po.warehouse
      ? {
          id: po.warehouse.warehouse_id,
          name: po.warehouse.warehouse_name,
          address: po.warehouse.address ?? null,
        }
      : null,
    customerBranch: po.customer_branch
      ? {
          id: po.customer_branch.branch_id,
          name: po.customer_branch.branch_name,
          organization: po.customer_branch.organization?.name ?? null,
          organizationId:
            po.customer_branch.organization?.organization_id ?? null,
          vatRegistration: po.customer_branch.vat_registration ?? null,
          companyRegistration: po.customer_branch.company_registration ?? null,
          address: po.customer_branch.address ?? null,
        }
      : null,
    supplierBranch: po.supplier_branch
      ? {
          id: po.supplier_branch.branch_id,
          name: po.supplier_branch.branch_name,
          organization: po.supplier_branch.organization?.name ?? null,
          organizationId:
            po.supplier_branch.organization?.organization_id ?? null,
          vatRegistration: po.supplier_branch.vat_registration ?? null,
          companyRegistration: po.supplier_branch.company_registration ?? null,
          address: po.supplier_branch.address ?? null,
        }
      : null,
  };
};

const normaliseQuote = (raw) => {
  const unwrapped = raw?.quotation ?? raw;
  const r = Array.isArray(unwrapped) ? unwrapped[0] : unwrapped;
  if (!r) return null;

  const statusName =
    r.quotation_status?.description ??
    r.quotation_status?.quotation_status_name ??
    r.status ??
    "—";

  const processedByValid =
    r.processed_by_user?.user_id != null || r.processed_by_user?.name != null;
  const approvedByValid =
    r.approved_by_user?.user_id != null || r.approved_by_user?.name != null;

  return {
    id: r.quotation_id,
    purchaseOrderId: r.purchase_order_id,
    documentNumber: r.document_number,
    processDate: r.process_date,
    dateApproved: r.date_approved ?? null,
    notes: r.notes ?? null,
    valueExcl: r.value_excl,
    valueIncl: r.value_incl,
    vatRate: normaliseVatRate(r.vat_rate),
    status: statusName,
    statusCode: statusName.toUpperCase(),
    statusId: r.quotation_status?.quotation_status_id,
    processedBy: processedByValid
      ? `${r.processed_by_user.name ?? ""} ${r.processed_by_user.surname ?? ""}`.trim()
      : null,
    processedByEmail: processedByValid
      ? (r.processed_by_user.email_address ?? null)
      : null,
    processedByPhone: processedByValid
      ? (r.processed_by_user.contact_number ?? null)
      : null,
    processedByUserId: processedByValid
      ? (r.processed_by_user.user_id ?? null)
      : null,
    approvedBy: approvedByValid
      ? `${r.approved_by_user.name ?? ""} ${r.approved_by_user.surname ?? ""}`.trim()
      : null,
    approvedByEmail: approvedByValid
      ? (r.approved_by_user.email_address ?? null)
      : null,
    approvedByPhone: approvedByValid
      ? (r.approved_by_user.contact_number ?? null)
      : null,
    approvedByUserId: approvedByValid
      ? (r.approved_by_user.user_id ?? null)
      : null,
    // Only populated when the by-id endpoint returns it (list endpoint never
    // includes this). Safe to leave null everywhere else in the app.
    purchaseOrder: normalisePoSnapshot(r.purchase_order),
    // Raw audit trail of status changes/notes on the quote, if returned.
    updates: Array.isArray(r.quotation_updates) ? r.quotation_updates : [],
    lines: (r.quotation_details ?? r.details ?? []).map((d) => ({
      id: d.quotation_detail_id,
      assetTypeId: d.asset_type_id,
      assetCode: d.asset_type_code,
      product: d.asset_description ?? d.asset_type_description,
      qty: d.qty_quoted ?? d.quantity,
      unitPriceExcl: d.unit_price_excl,
      unitPriceIncl: d.unit_price_incl,
      lineTotalExcl: d.line_total_excl,
      lineTotalIncl: d.line_total_incl,
      vatRate: normaliseVatRate(d.vat_rate),
    })),
    invoiceUrl: r.invoice_url ?? null,
  };
};

const normaliseInvoice = (raw) => {
  const r = Array.isArray(raw) ? raw[0] : raw;
  if (!r) return null;
  return {
    id: r.invoice_id,
    sageInvoiceId: r.sage_invoice_id,
    quotationId: r.quotation_id ?? r.quotation?.quotation_id,
    quotationDocNumber: r.quotation?.document_number ?? null,
    quotationValueExcl: r.quotation?.value_excl ?? null,
    quotationValueIncl: r.quotation?.value_incl ?? null,
    quotationPurchaseOrderId: r.quotation?.purchase_order_id ?? null,
    sageInvoiceNumber: r.sage_invoice_number,
    sageDeliveryNoteNumber: r.sage_delivery_note_number,
    processDate: r.process_date,
    invoiceDate: r.invoice_date,
    valueExcl: r.value_excl,
    valueIncl: r.value_incl,
    vatRate: normaliseVatRate(r.vat_rate),
    notes: r.notes ?? null,
    status: r.invoice_status?.description ?? r.status?.description ?? "—",
    statusId: r.invoice_status?.invoice_status_id ?? null,
    processedBy: r.processed_by
      ? `${r.processed_by.name} ${r.processed_by.surname}`.trim()
      : r.processed_by_user
        ? `${r.processed_by_user.name} ${r.processed_by_user.surname}`.trim()
        : null,
    processedByEmail:
      r.processed_by?.email_address ??
      r.processed_by_user?.email_address ??
      null,
    processedByPhone:
      r.processed_by?.contact_number ??
      r.processed_by_user?.contact_number ??
      null,
    processedByUserId:
      r.processed_by?.user_id ?? r.processed_by_user?.user_id ?? null,
    lines: (r.details ?? []).map((d) => ({
      id: d.invoice_detail_id,
      assetTypeId: d.asset_type_id,
      assetCode: d.asset_type_code,
      product: d.asset_type_description ?? d.asset_description,
      qty: d.quantity ?? d.qty_quoted,
      qtyDelivered: d.qty_delivered ?? null,
      unitPriceExcl: d.unit_price_excl,
      unitPriceIncl: d.unit_price_incl,
      lineTotalExcl: d.line_total_excl,
      lineTotalIncl: d.line_total_incl,
      vatRate: normaliseVatRate(d.vat_rate),
    })),
  };
};

// ─── Status pipeline ──────────────────────────────────────────────────────────
const PIPELINE = [
  "Pending",
  "Confirmed",
  "Processing",
  "In Transit",
  "Delivered",
  "Cancelled",
];
const STATUS_FLOW = {
  Pending: ["Confirmed"],
  Confirmed: ["Processing"],
  Processing: ["In Transit"],
  "In Transit": ["Delivered"],
};
// A purchase order can only be cancelled while it's still in its initial
// "New" state (purchase_order_status_id === 1). Any other status id means
// it has already progressed past New, or has already been cancelled — in
// either case the Cancel action must be hidden, regardless of permissions.
const CANCELLABLE_STATUS_ID = 1;
const isOrderCancellable = (order) =>
  Number(order?.statusId) === CANCELLABLE_STATUS_ID;

// ─── Shared styles ────────────────────────────────────────────────────────────
const btn = (variant = "default") => ({
  padding: "8px 16px",
  borderRadius: "var(--r8)",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  transition: "opacity .15s",
  ...(variant === "primary" && {
    border: "none",
    background: "var(--flame)",
    color: "#fff",
  }),
  ...(variant === "ghost" && {
    border: "1px solid var(--bright)",
    background: "transparent",
    color: "var(--text2)",
  }),
  ...(variant === "danger" && {
    border: "1px solid rgba(220,53,69,.4)",
    background: "transparent",
    color: "#e05260",
  }),
  ...(variant === "success" && {
    border: "none",
    background: "#198754",
    color: "#fff",
  }),
  ...(variant === "warning" && {
    border: "1px solid rgba(255,193,7,.4)",
    background: "transparent",
    color: "#ffc107",
  }),
  ...(variant === "outline-flame" && {
    border: "1px solid var(--flame)",
    background: "transparent",
    color: "var(--flame)",
  }),
  ...(variant === "default" && {
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text2)",
  }),
  ...(variant === "info" && {
    border: "1px solid rgba(77,163,255,.4)",
    background: "transparent",
    color: "#4da3ff",
  }),
});

const inputStyle = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--r8)",
  padding: "10px 12px",
  color: "var(--text1)",
  fontSize: 12,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

// ─── ActionMenu ───────────────────────────────────────────────────────────────
function ActionMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [mounted, setMounted] = useState(false);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!open) return;
      if (
        wrapperRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const handleToggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const menuWidth = 170;
      const left = Math.min(
        Math.max(rect.left, 8),
        window.innerWidth - menuWidth - 8,
      );
      setPos({ top: rect.bottom + 6, left });
    }
    setOpen((prev) => !prev);
  }, []);

  const menu = open ? (
    <div
      className="adrop"
      ref={menuRef}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        right: "auto",
        zIndex: 9999,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item, i) => (
        <div
          key={i}
          className={`adrop-item${item.danger ? " red" : ""}`}
          onClick={() => {
            setOpen(true);
            item.action?.();
          }}
        >
          {item.icon && <span>{item.icon}</span>}
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  ) : null;

  return (
    <div className="action-wrap" ref={wrapperRef}>
      {mounted && menu && createPortal(menu, document.body)}
    </div>
  );
}

// ─── QuoteBadge ───────────────────────────────────────────────────────────────
function QuoteBadge({ status }) {
  const s = (status ?? "").toLowerCase();
  let bg, color, border;
  if (["approved", "accepted"].includes(s)) {
    bg = "rgba(25,135,84,.12)";
    color = "#4caf86";
    border = "1px solid rgba(25,135,84,.35)";
  } else if (["rejected", "declined", "cancelled"].includes(s)) {
    bg = "rgba(220,53,69,.12)";
    color = "#e05260";
    border = "1px solid rgba(220,53,69,.35)";
  } else {
    bg = "rgba(77,163,255,.12)";
    color = "#4da3ff";
    border = "1px solid rgba(77,163,255,.35)";
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 90,
        padding: "3px 10px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: ".04em",
        background: bg,
        color,
        border,
        whiteSpace: "nowrap",
      }}
    >
      {status ?? "—"}
    </span>
  );
}

// ─── ErrorState ───────────────────────────────────────────────────────────────
function ErrorState({ title = "Something went wrong", message, onRetry }) {
  return (
    <div className="panel">
      <div className="pb">
        <div className="empty" style={{ padding: "32px 0" }}>
          <div className="empty-ico">
            <i className="bi bi-exclamation-triangle" />
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            {title}
          </div>
          <div
            className="fs11 tc3"
            style={{
              marginBottom: onRetry ? 16 : 0,
              maxWidth: 360,
              margin: "0 auto",
            }}
          >
            {message}
          </div>
          {onRetry && (
            <button
              className="btn btn-g btn-sm"
              style={{ marginTop: 14 }}
              onClick={onRetry}
            >
              <i className="bi bi-arrow-clockwise" /> Try Again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── PageShell ────────────────────────────────────────────────────────────────
function PageShell({
  title,
  subtitle,
  badge,
  onBack,
  backLabel = "Back to Orders",
  headerRight,
  children,
}) {
  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <div className="fb mb-22" style={{ alignItems: "center" }}>
        <div>
          <div className="ph-title" style={{ fontSize: 18 }}>
            {title}
          </div>
          {subtitle && (
            <div className="hdr-crumb" style={{ marginTop: 4 }}>
              {subtitle}
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 4,
          }}
        >
          {headerRight}
          {onBack && (
            <button className="back-btn" onClick={onBack}>
              {backLabel}
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

// ─── DownloadPdfButton ────────────────────────────────────────────────────────
function DownloadPdfButton({ quotationId, invoiceId, filename, style, toast }) {
  const [downloading, setDownloading] = useState(false);

  const url =
    quotationId != null
      ? `${CDN_BASE}/quotation?quotation_id=${quotationId}`
      : `${CDN_BASE}/invoice?invoice_id=${invoiceId}`;

  const defaultFilename =
    quotationId != null
      ? `quote-${quotationId}.pdf`
      : `invoice-${invoiceId}.pdf`;

  const handleClick = async () => {
    setDownloading(true);
    try {
      await downloadPdfFile(url, filename ?? defaultFilename);
    } catch (e) {
      toast
        ? toast(`PDF download failed: ${e.message}`, "e")
        : console.error("PDF download failed:", e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={downloading}
      style={{ ...style, opacity: downloading ? 0.65 : 1 }}
    >
      <i className="bi bi-file-earmark-pdf" style={{ fontSize: 11 }} />
      {downloading ? "Downloading…" : "Download PDF"}
    </button>
  );
}

// ─── LineItemsTotals ────────────────────────────────────────────────────────
function LineItemsTotals({ valueExcl, valueIncl, vatRate }) {
  const vatAmount =
    valueExcl != null && vatRate != null
      ? valueExcl * (vatRate / 100)
      : valueIncl != null && valueExcl != null
        ? valueIncl - valueExcl
        : null;
  return (
    <div
      style={{
        margin: "0 0 0 0",
        borderTop: "1px solid var(--border)",
        background: "rgba(255,255,255,.02)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 16px",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <span style={{ fontSize: 12, color: "var(--text3)", fontWeight: 600 }}>
          Value Excl. VAT
        </span>
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            fontFamily: "monospace",
            color: "var(--text2)",
          }}
        >
          {fmt.currency(valueExcl)}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 16px",
          borderBottom: "1px solid var(--border)",
          background: "rgba(255,193,7,.03)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{ fontSize: 12, color: "var(--text3)", fontWeight: 600 }}
          >
            VAT
          </span>
          {vatRate != null && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: "2px 7px",
                borderRadius: 5,
                background: "rgba(255,193,7,.12)",
                color: "#ffc107",
                border: "1px solid rgba(255,193,7,.3)",
                letterSpacing: ".04em",
              }}
            >
              {vatRate}%
            </span>
          )}
        </div>
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            fontFamily: "monospace",
            color: "var(--text2)",
          }}
        >
          {vatAmount != null ? fmt.currency(vatAmount) : "—"}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          background: "rgba(255,79,23,.04)",
        }}
      >
        <span style={{ fontSize: 13, color: "var(--text1)", fontWeight: 800 }}>
          Value Incl. VAT
        </span>
        <span
          style={{
            fontSize: 20,
            fontWeight: 800,
            fontFamily: "monospace",
            color: "var(--text1)",
          }}
        >
          {fmt.currency(valueIncl)}
        </span>
      </div>
    </div>
  );
}

// ─── PersonCard ───────────────────────────────────────────────────────────────
function PersonCard({ title, name, email, phone, userId }) {
  if (!name && !email && !phone && userId == null) return null;
  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column" }}>
      <div className="ph">
        <span className="ph-title">{title}</span>
      </div>
      <div className="pb" style={{ flex: 1 }}>
        <div className="review-block">
          {[
            ["Name", name],
            ["Email", email],
            ["Phone", phone],
          ]
            .filter(([, v]) => v != null && v !== "")
            .map(([k, v]) => (
              <div key={k} className="review-row">
                <span className="review-label">{k}</span>
                <span className="review-value">{v}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

// ─── InvoiceDetailPanel ───────────────────────────────────────────────────────
function InvoiceDetailPanel({ invoice, onClose, toast }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <button
          className="btn btn-g btn-sm btn-ico"
          onClick={onClose}
          title="Back to Invoice List"
        >
          <i className="bi bi-arrow-left" style={{ fontSize: 12 }} />
        </button>
        <span className="ph-title" style={{ fontSize: 13 }}>
          Invoice #{invoice.id}
        </span>
        <div style={{ marginLeft: "auto" }}>
          <DownloadPdfButton
            invoiceId={invoice.id}
            filename={`invoice-${invoice.id}.pdf`}
            style={{ ...btn("outline-flame"), fontSize: 11 }}
            toast={toast}
          />
        </div>
      </div>
      <div
        className="equal-row"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 16,
          alignItems: "stretch",
        }}
      >
        <div
          className="panel"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className="ph">
            <span className="ph-title">Invoice Details</span>
          </div>
          <div className="pb" style={{ flex: 1 }}>
            <div className="review-block">
              {[
                ["Invoice ID", invoice.id],
                ["Sage Invoice ID", invoice.sageInvoiceId],
                [
                  "Invoice Date",
                  invoice.invoiceDate ? fmt.date(invoice.invoiceDate) : null,
                ],
                [
                  "Process Date",
                  invoice.processDate ? fmt.date(invoice.processDate) : null,
                ],
                ["Status", invoice.status],
                ["Notes", invoice.notes],
              ]
                .filter(([, v]) => v != null && v !== "")
                .map(([k, v]) => (
                  <div key={k} className="review-row">
                    <span className="review-label">{k}</span>
                    <span className="review-value">{v}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
        <PersonCard
          title="Processed By"
          name={invoice.processedBy}
          email={invoice.processedByEmail}
          phone={invoice.processedByPhone}
        />
        {invoice.quotationId && (
          <div
            className="panel"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <div className="ph">
              <span className="ph-title">Linked Quotation</span>
            </div>
            <div className="pb" style={{ flex: 1 }}>
              <div className="review-block">
                {[
                  ["Quotation ID", invoice.quotationId],
                  ["Document #", invoice.quotationDocNumber],
                  ["Purchase Order", invoice.quotationPurchaseOrderId],
                  [
                    "Quote Excl. VAT",
                    invoice.quotationValueExcl != null
                      ? fmt.currency(invoice.quotationValueExcl)
                      : null,
                  ],
                  [
                    "Quote Incl. VAT",
                    invoice.quotationValueIncl != null
                      ? fmt.currency(invoice.quotationValueIncl)
                      : null,
                  ],
                ]
                  .filter(([, v]) => v != null && v !== "")
                  .map(([k, v]) => (
                    <div key={k} className="review-row">
                      <span className="review-label">{k}</span>
                      <span className="review-value">{v}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {invoice.lines?.length > 0 && (
        <div className="panel">
          <div className="ph">
            <span className="ph-title">
              Line Items ({invoice.lines.length})
            </span>
          </div>
          <div className="pb" style={{ padding: 0 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Detail ID</th>
                  <th>Code</th>
                  <th>Description</th>
                  <th style={{ textAlign: "right" }}>Qty</th>
                  <th style={{ textAlign: "right" }}>Qty Delivered</th>
                  <th style={{ textAlign: "right" }}>Unit (excl)</th>
                  <th style={{ textAlign: "right" }}>Unit (incl)</th>
                  <th style={{ textAlign: "right" }}>Line Total (excl)</th>
                  <th style={{ textAlign: "right" }}>Line Total (incl)</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lines.map((l, i) => (
                  <tr key={l.id ?? i}>
                    <td className="mono fs10 tc3">{l.id ?? "—"}</td>
                    <td className="mono fs10 tc3">{l.assetCode ?? "—"}</td>
                    <td>{l.product ?? "—"}</td>
                    <td className="mono" style={{ textAlign: "right" }}>
                      {l.qty ?? "—"}
                    </td>
                    <td className="mono tc3" style={{ textAlign: "right" }}>
                      {l.qtyDelivered ?? "—"}
                    </td>
                    <td className="mono" style={{ textAlign: "right" }}>
                      {fmt.currency(l.unitPriceExcl)}
                    </td>
                    <td className="mono" style={{ textAlign: "right" }}>
                      {fmt.currency(l.unitPriceIncl)}
                    </td>
                    <td className="mono" style={{ textAlign: "right" }}>
                      {fmt.currency(l.lineTotalExcl)}
                    </td>
                    <td className="mono fw-6" style={{ textAlign: "right" }}>
                      {fmt.currency(l.lineTotalIncl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <LineItemsTotals
              valueExcl={invoice.valueExcl}
              valueIncl={invoice.valueIncl}
              vatRate={invoice.vatRate}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── QuoteViewPage ───────────────────────────────────────────────────────────
function QuoteViewPage({ order, onBack, showToast: toast }) {
  const [quoteList, setQuoteList] = useState([]);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deciding, setDeciding] = useState(null);
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const isSupplier = useRole() === "supplier";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        // The by-purchase-order list is used ONLY to figure out which
        // quotation_id is relevant (approved one, else the latest) — the
        // actual quote data rendered on this page always comes from the
        // by-id call below via doLoadDetail.
        const list = await getQuoteByPurchaseOrder(order._id);
        if (!cancelled) {
          setQuoteList(list);
          if (list.length === 0) {
            setLoading(false);
          } else if (list.length === 1) {
            await doLoadDetail(list[0].quotation_id, () => cancelled);
          } else {
            const approved = list.find(
              (q) => q.quotation_status?.quotation_status_id === 2,
            );
            const toLoad = approved ?? list[list.length - 1];
            await doLoadDetail(toLoad.quotation_id, () => cancelled);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order._id, reloadKey]);

  const doLoadDetail = async (quotation_id, isCancelled = () => false) => {
    setDetailLoading(true);
    setLoading(true);
    setQuote(null);
    try {
      const raw = await getQuoteById(quotation_id);
      if (!isCancelled()) setQuote(normaliseQuote(raw));
    } catch (e) {
      if (!isCancelled()) setError(e.message);
    } finally {
      if (!isCancelled()) {
        setDetailLoading(false);
        setLoading(false);
      }
    }
  };

  const doDecision = async (status) => {
    setDeciding(status);
    try {
      await updateQuoteStatus(quote.id, status === "APPROVED" ? 2 : 3, notes);
      toast?.(
        status === "APPROVED"
          ? "Quote accepted — supplier notified ✓"
          : "Quote declined",
        status === "APPROVED" ? "s" : "w",
      );
      await doLoadDetail(quote.id);
    } catch (e) {
      toast?.(`Failed: ${e.message}`, "e");
    } finally {
      setDeciding(null);
    }
  };

  const statusCode = (quote?.statusCode ?? quote?.status ?? "").toLowerCase();
  const isPending = [
    "pending",
    "new",
    "submitted",
    "sent",
    "awaiting approval",
    "1",
  ].includes(statusCode);
  const isApproved = ["approved", "accepted"].includes(statusCode);
  const isRejected = ["rejected", "declined"].includes(statusCode);

  return (
    // ── FIX 1: PDF button lives in the fb mb-22 header via headerRight ──────
    <PageShell
      title={`Quote — ${order.id}`}
      subtitle="Quotations for this Purchase Order"
      badge={quote ? <QuoteBadge status={quote.status} /> : null}
      onBack={onBack}
      backLabel="Back to Orders"
      headerRight={
        quote ? (
          <DownloadPdfButton
            quotationId={quote.id}
            filename={`quote-${quote.documentNumber ?? quote.id}.pdf`}
            style={{ ...btn("outline-flame"), fontSize: 11 }}
            toast={toast}
          />
        ) : null
      }
    >
      <div
        style={{
          marginTop: 24,
          paddingTop: 16,
          borderTop: "1px solid var(--border)",
        }}
      >
        <button className="back-btn" onClick={onBack}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            color="#99E4D5"
          >
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Orders
        </button>
      </div>

      {quoteList.length > 1 && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="ph">
            <span className="ph-title">Quotations ({quoteList.length})</span>
          </div>
          <div className="pb" style={{ padding: 0 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Quotation ID</th>
                  <th>Document #</th>
                  <th>Process Date</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Excl. VAT</th>
                  <th style={{ textAlign: "right" }}>Incl. VAT</th>
                </tr>
              </thead>
              <tbody>
                {quoteList.map((q) => {
                  const statusName = q.quotation_status?.description ?? "—";
                  const isSelected = quote?.id === q.quotation_id;
                  return (
                    <tr
                      key={q.quotation_id}
                      onClick={() =>
                        !detailLoading && doLoadDetail(q.quotation_id)
                      }
                      style={{
                        background: isSelected
                          ? "rgba(255,79,23,.05)"
                          : undefined,
                        cursor: detailLoading ? "wait" : "pointer",
                        outline: isSelected
                          ? "1px solid rgba(255,79,23,.2)"
                          : "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected)
                          e.currentTarget.style.background =
                            "rgba(255,255,255,.03)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = isSelected
                          ? "rgba(255,79,23,.05)"
                          : "";
                      }}
                    >
                      <td className="mono t-flame">{q.quotation_id}</td>
                      <td className="mono fs10 tc3">{q.document_number}</td>
                      <td className="mono fs10 tc3">
                        {q.process_date ? fmt.date(q.process_date) : "—"}
                      </td>
                      <td>
                        <QuoteBadge status={statusName} />
                      </td>
                      <td className="mono" style={{ textAlign: "right" }}>
                        {fmt.currency(q.value_excl)}
                      </td>
                      <td className="mono fw-6" style={{ textAlign: "right" }}>
                        {fmt.currency(q.value_incl)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && (
        <div
          className="panel"
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--text3)",
            fontSize: 13,
          }}
        >
          <span
            style={{
              animation: "spin 1s linear infinite",
              display: "inline-block",
              marginRight: 8,
            }}
          >
            ⟳
          </span>
          {quoteList.length > 1 ? "Loading quote detail…" : "Loading quote…"}
        </div>
      )}
      {error && (
        <ErrorState
          message={error}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      )}

      {!loading && !error && quoteList.length === 0 && (
        <div className="panel">
          <div className="pb">
            <div className="empty" style={{ padding: "32px 0" }}>
              <div className="empty-ico">
                <i className="bi bi-file-earmark-x" />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                No Quote Found
              </div>
              <div className="fs11 tc3">
                The supplier hasn't created a quote for this order yet.
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && quote && (
        <>
          {!isSupplier && (
            <div style={{ marginBottom: 16 }}>
              {isApproved && (
                <div
                  style={{
                    padding: "16px 20px",
                    background: "rgba(25,135,84,.08)",
                    border: "1px solid rgba(25,135,84,.3)",
                    borderRadius: "var(--r8)",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    color: "#4caf86",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {/* PDF button removed here — it now lives in the page header */}
                  <span>
                    You approved this quote. The supplier has been notified and
                    will create an invoice.
                  </span>
                </div>
              )}
              {isRejected && (
                <div
                  style={{
                    padding: "16px 20px",
                    background: "rgba(220,53,69,.08)",
                    border: "1px solid rgba(220,53,69,.3)",
                    borderRadius: "var(--r8)",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    color: "#e05260",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  You declined this quote.
                </div>
              )}
              {isPending && (
                <div
                  style={{
                    padding: 20,
                    background: "rgba(255,193,7,.06)",
                    border: "1px solid rgba(255,193,7,.3)",
                    borderRadius: "var(--r8)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "var(--text1)",
                      marginBottom: 6,
                    }}
                  >
                    Awaiting Your Decision
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--text3)",
                      marginBottom: 16,
                    }}
                  >
                    Review the line items below, then approve or decline.
                  </div>
                  {showNotes && (
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Optional notes"
                      rows={2}
                      style={{
                        ...inputStyle,
                        marginBottom: 14,
                        resize: "vertical",
                      }}
                    />
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      style={{
                        ...btn("success"),
                        padding: "10px 22px",
                        fontSize: 13,
                      }}
                      disabled={!!deciding}
                      onClick={() => doDecision("APPROVED")}
                    >
                      {deciding === "APPROVED"
                        ? "Approving…"
                        : "✓ Approve Quote"}
                    </button>
                    <button
                      style={{
                        ...btn("danger"),
                        padding: "10px 22px",
                        fontSize: 13,
                      }}
                      disabled={!!deciding}
                      onClick={() => doDecision("REJECTED")}
                    >
                      {deciding === "REJECTED"
                        ? "Declining…"
                        : "✕ Decline Quote"}
                    </button>
                    <button
                      style={{ ...btn("ghost"), fontSize: 11 }}
                      onClick={() => setShowNotes((v) => !v)}
                    >
                      {showNotes ? "Hide Notes" : "Add Notes"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {isSupplier && isApproved && (
            <div
              style={{
                padding: "14px 20px",
                background: "rgba(25,135,84,.08)",
                border: "1px solid rgba(25,135,84,.3)",
                borderRadius: "var(--r8)",
                marginBottom: 16,
                fontSize: 13,
                color: "#4caf86",
                fontWeight: 600,
              }}
            >
              Customer approved this quote. You can now create the final
              invoice.
            </div>
          )}

          <div
            className="equal-row"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
              marginBottom: 16,
              alignItems: "stretch",
            }}
          >
            <div
              className="panel"
              style={{ display: "flex", flexDirection: "column" }}
            >
              <div className="ph">
                <span className="ph-title">Quote Details</span>
              </div>
              <div className="pb" style={{ flex: 1 }}>
                <div className="review-block">
                  {[
                    ["Document Number", quote.documentNumber],
                    ["Status", quote.status],
                    [
                      "Process Date",
                      quote.processDate ? fmt.date(quote.processDate) : null,
                    ],
                    [
                      "Date Approved",
                      quote.dateApproved ? fmt.date(quote.dateApproved) : null,
                    ],
                    [
                      "VAT Rate",
                      quote.vatRate != null ? `${quote.vatRate}%` : null,
                    ],
                    [
                      "Value Excl. VAT",
                      quote.valueExcl != null
                        ? fmt.currency(quote.valueExcl)
                        : null,
                    ],
                    [
                      "Value Incl. VAT",
                      quote.valueIncl != null
                        ? fmt.currency(quote.valueIncl)
                        : null,
                    ],
                    ["Invoice URL", quote.invoiceUrl || null],
                    ["Notes", quote.notes || null],
                  ]
                    .filter(([, v]) => v != null && v !== "")
                    .map(([k, v]) => (
                      <div key={k} className="review-row">
                        <span className="review-label">{k}</span>
                        <span className="review-value">{v}</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
            <PersonCard
              title="Processed By"
              name={quote.processedBy}
              email={quote.processedByEmail}
              phone={quote.processedByPhone}
              userId={quote.processedByUserId}
            />
            <PersonCard
              title="Approved By"
              name={quote.approvedBy}
              email={quote.approvedByEmail}
              phone={quote.approvedByPhone}
              userId={quote.approvedByUserId}
            />
          </div>

          {quote.purchaseOrder && (
            <div
              className="equal-row"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 16,
                marginBottom: 16,
                alignItems: "stretch",
              }}
            >
              <div
                className="panel"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <div className="ph">
                  <span className="ph-title">Linked Purchase Order</span>
                </div>
                <div className="pb" style={{ flex: 1 }}>
                  <div className="review-block">
                    {[
                      ["PO Number", quote.purchaseOrder.number],
                      [
                        "Order Date",
                        quote.purchaseOrder.orderDate
                          ? fmt.date(quote.purchaseOrder.orderDate)
                          : null,
                      ],
                      [
                        "Required By",
                        quote.purchaseOrder.requiredDeliveryDate
                          ? fmt.date(quote.purchaseOrder.requiredDeliveryDate)
                          : null,
                      ],
                    ]
                      .filter(([, v]) => v != null && v !== "")
                      .map(([k, v]) => (
                        <div key={k} className="review-row">
                          <span className="review-label">{k}</span>
                          <span className="review-value">{v}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              {quote.purchaseOrder.warehouse && (
                <div
                  className="panel"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <div className="ph">
                    <span className="ph-title">Warehouse</span>
                  </div>
                  <div className="pb" style={{ flex: 1 }}>
                    <div className="review-block">
                      {[
                        ["Name", quote.purchaseOrder.warehouse.name],
                        ...addressRows(quote.purchaseOrder.warehouse.address),
                      ]
                        .filter(([, v]) => v != null && v !== "")
                        .map(([k, v]) => (
                          <div key={k} className="review-row">
                            <span className="review-label">{k}</span>
                            <span className="review-value">{v}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
              {quote.purchaseOrder.customerBranch && (
                <div
                  className="panel"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <div className="ph">
                    <span className="ph-title">Customer Branch</span>
                  </div>
                  <div className="pb" style={{ flex: 1 }}>
                    <div className="review-block">
                      {[
                        ["Branch", quote.purchaseOrder.customerBranch.name],
                        [
                          "Organization",
                          quote.purchaseOrder.customerBranch.organization,
                        ],
                        [
                          "VAT Reg",
                          quote.purchaseOrder.customerBranch.vatRegistration,
                        ],
                        [
                          "Company Reg",
                          quote.purchaseOrder.customerBranch
                            .companyRegistration,
                        ],
                        ...addressRows(
                          quote.purchaseOrder.customerBranch.address,
                        ),
                      ]
                        .filter(([, v]) => v != null && v !== "")
                        .map(([k, v]) => (
                          <div key={k} className="review-row">
                            <span className="review-label">{k}</span>
                            <span className="review-value">{v}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
              {quote.purchaseOrder.supplierBranch && (
                <div
                  className="panel"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <div className="ph">
                    <span className="ph-title">Supplier Branch</span>
                  </div>
                  <div className="pb" style={{ flex: 1 }}>
                    <div className="review-block">
                      {[
                        ["Branch", quote.purchaseOrder.supplierBranch.name],
                        [
                          "Organization",
                          quote.purchaseOrder.supplierBranch.organization,
                        ],
                        [
                          "VAT Reg",
                          quote.purchaseOrder.supplierBranch.vatRegistration,
                        ],
                        [
                          "Company Reg",
                          quote.purchaseOrder.supplierBranch
                            .companyRegistration,
                        ],
                        ...addressRows(
                          quote.purchaseOrder.supplierBranch.address,
                        ),
                      ]
                        .filter(([, v]) => v != null && v !== "")
                        .map(([k, v]) => (
                          <div key={k} className="review-row">
                            <span className="review-label">{k}</span>
                            <span className="review-value">{v}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {quote.lines?.length > 0 ? (
            <div className="panel" style={{ marginBottom: 16 }}>
              <div className="ph">
                <span className="ph-title">
                  Line Items ({quote.lines.length})
                </span>
              </div>
              <div className="pb" style={{ padding: 0 }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Description</th>
                      <th style={{ textAlign: "right" }}>Qty</th>
                      <th style={{ textAlign: "right" }}>Unit (excl)</th>
                      <th style={{ textAlign: "right" }}>Unit (incl)</th>
                      <th style={{ textAlign: "right" }}>Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quote.lines.map((l, i) => (
                      <tr key={l.id ?? i}>
                        <td className="mono fs10 tc3">{l.assetCode ?? "—"}</td>
                        <td>{l.product ?? "—"}</td>
                        <td className="mono" style={{ textAlign: "right" }}>
                          {l.qty ?? "—"}
                        </td>
                        <td className="mono" style={{ textAlign: "right" }}>
                          {fmt.currency(l.unitPriceExcl)}
                        </td>
                        <td className="mono" style={{ textAlign: "right" }}>
                          {fmt.currency(l.unitPriceIncl)}
                        </td>
                        <td
                          className="mono fw-6"
                          style={{ textAlign: "right" }}
                        >
                          {fmt.currency(l.lineTotalIncl)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <LineItemsTotals
                  valueExcl={quote.valueExcl}
                  valueIncl={quote.valueIncl}
                  vatRate={quote.vatRate}
                />
              </div>
            </div>
          ) : (
            <div className="panel" style={{ marginBottom: 16 }}>
              <div className="pb">
                <div className="empty" style={{ padding: "16px 0" }}>
                  <div className="fs11 tc3">
                    No line items on this quotation.
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </PageShell>
  );
}

// ─── InvoiceViewPage ──────────────────────────────────────────────────────────
function InvoiceViewPage({ order, onBack, showToast: toast }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const raw = await getInvoicesByPurchaseOrder(order._id);
        if (!cancelled) {
          const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
          const normalised = list.map(normaliseInvoice).filter(Boolean);
          setInvoices(normalised);
          if (normalised.length === 1) setSelectedInvoice(normalised[0]);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order._id, reloadKey]);

  return (
    <PageShell
      title={`Invoices — ${order.id}`}
      subtitle="All invoices linked to this purchase order"
      onBack={onBack}
      backLabel="Back to Orders"
    >
      <div
        style={{
          marginTop: 24,
          paddingTop: 16,
          borderTop: "1px solid var(--border)",
        }}
      >
        <button className="back-btn" onClick={onBack}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            color="#99E4D5"
          >
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Orders
        </button>
      </div>

      {loading && (
        <div
          className="panel"
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--text3)",
            fontSize: 13,
          }}
        >
          <span
            style={{
              animation: "spin 1s linear infinite",
              display: "inline-block",
              marginRight: 8,
            }}
          >
            ⟳
          </span>
          Loading invoices…
        </div>
      )}
      {error && (
        <ErrorState
          message={error}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      )}

      {!loading && !error && invoices.length === 0 && (
        <div className="panel">
          <div className="pb">
            <div className="empty" style={{ padding: "32px 0" }}>
              <div className="empty-ico">
                <i className="bi bi-receipt" />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                No Invoices Yet
              </div>
              <div className="fs11 tc3">
                No invoices have been created for this purchase order.
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && invoices.length > 0 && (
        <>
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="ph">
              <span className="ph-title">Invoices ({invoices.length})</span>
            </div>
            <div className="pb" style={{ padding: 0 }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Invoice ID</th>
                    <th>Invoice Date</th>
                    <th>Status</th>
                    <th style={{ textAlign: "right" }}>Excl. VAT</th>
                    <th style={{ textAlign: "right" }}>Incl. VAT</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, i) => {
                    const isSelected = selectedInvoice?.id === inv.id;
                    return (
                      <tr
                        key={inv.id ?? i}
                        onClick={() =>
                          setSelectedInvoice(isSelected ? null : inv)
                        }
                        style={{
                          background: isSelected
                            ? "rgba(255,79,23,.05)"
                            : undefined,
                          cursor: "pointer",
                          outline: isSelected
                            ? "1px solid rgba(255,79,23,.2)"
                            : "none",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected)
                            e.currentTarget.style.background =
                              "rgba(255,255,255,.03)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = isSelected
                            ? "rgba(255,79,23,.05)"
                            : "";
                        }}
                      >
                        <td className="mono t-flame">{inv.id ?? "—"}</td>
                        <td className="mono fs10 tc3">
                          {inv.invoiceDate ? fmt.date(inv.invoiceDate) : "—"}
                        </td>
                        <td>
                          <QuoteBadge status={inv.status ?? "—"} />
                        </td>
                        <td className="mono" style={{ textAlign: "right" }}>
                          {fmt.currency(inv.valueExcl)}
                        </td>
                        <td
                          className="mono fw-6"
                          style={{ textAlign: "right" }}
                        >
                          {fmt.currency(inv.valueIncl)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {selectedInvoice && (
            <InvoiceDetailPanel
              invoice={selectedInvoice}
              onClose={() => setSelectedInvoice(null)}
              toast={toast}
            />
          )}
        </>
      )}
    </PageShell>
  );
}

// ─── QuoteDecisionBanner ──────────────────────────────────────────────────────
function QuoteDecisionBanner({ quote, onDecision, toast }) {
  const [deciding, setDeciding] = useState(null);
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);

  const { canUpdateQuotations } = usePermissions();

  const sc = (quote.statusCode ?? quote.status ?? "").toLowerCase();
  const isPending = [
    "pending",
    "new",
    "submitted",
    "sent",
    "awaiting approval",
    "1",
  ].includes(sc);
  const isApproved = ["approved", "accepted"].includes(sc);
  const isRejected = ["rejected", "declined"].includes(sc);

  if (isApproved)
    return (
      <div
        style={{
          padding: "14px 16px",
          background: "rgba(25,135,84,.08)",
          border: "1px solid rgba(25,135,84,.3)",
          borderRadius: "var(--r8)",
          marginBottom: 14,
          display: "flex",
          alignItems: "center",
          gap: 10,
          color: "#4caf86",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        You accepted this quote. The supplier has been notified and will create
        an invoice.
      </div>
    );

  if (isRejected)
    return (
      <div
        style={{
          padding: "14px 16px",
          background: "rgba(220,53,69,.08)",
          border: "1px solid rgba(220,53,69,.3)",
          borderRadius: "var(--r8)",
          marginBottom: 14,
          display: "flex",
          alignItems: "center",
          gap: 10,
          color: "#e05260",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        You declined this quote.
      </div>
    );

  if (!isPending) return null;

  if (!canUpdateQuotations)
    return (
      <div
        style={{
          padding: "14px 16px",
          background: "rgba(255,193,7,.06)",
          border: "1px solid rgba(255,193,7,.25)",
          borderRadius: "var(--r8)",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "var(--text1)",
            marginBottom: 4,
          }}
        >
          Awaiting Decision
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)" }}>
          This quote is pending approval. You don't have permission to approve
          or decline it.
        </div>
      </div>
    );

  const doDecision = async (status) => {
    setDeciding(status);
    try {
      await updateQuoteStatus(quote.id, status === "APPROVED" ? 2 : 3, notes);
      toast?.(
        status === "APPROVED"
          ? "Quote accepted — supplier notified ✓"
          : "Quote declined",
        status === "APPROVED" ? "s" : "w",
      );
      onDecision?.(status);
    } catch (e) {
      toast?.(`Failed: ${e.message}`, "e");
    } finally {
      setDeciding(null);
    }
  };

  return (
    <div
      style={{
        padding: 16,
        background: "rgba(255,193,7,.06)",
        border: "1px solid rgba(255,193,7,.25)",
        borderRadius: "var(--r8)",
        marginBottom: 14,
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: "var(--text1)",
          marginBottom: 8,
        }}
      >
        This quote is awaiting your decision
      </div>
      <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 14 }}>
        Review the line items above, then accept or decline.
      </div>
      {showNotes && (
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes"
          rows={2}
          style={{ ...inputStyle, marginBottom: 12, resize: "vertical" }}
        />
      )}
      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <button
          className="btn btn-success"
          disabled={!!deciding}
          onClick={() => doDecision("APPROVED")}
        >
          {deciding === "APPROVED" ? "Accepting…" : "✓ Accept Quote"}
        </button>
        <button
          className="btn btn-d"
          disabled={!!deciding}
          onClick={() => doDecision("REJECTED")}
        >
          {deciding === "REJECTED" ? "Declining…" : "✕ Decline Quote"}
        </button>
        <button
          style={{ ...btn("ghost"), fontSize: 11 }}
          onClick={() => setShowNotes((v) => !v)}
        >
          {showNotes ? "Hide Notes" : "Add Notes"}
        </button>
      </div>
    </div>
  );
}

// ─── CreateQuoteModal ─────────────────────────────────────────────────────────
function CreateQuoteModal({ order, onClose, onCreated, showToast: toast }) {
  const emptyLine = {
    assetCode: "",
    product: "",
    qty: 1,
    unitPriceExcl: "",
    vatRate: 15,
    assetTypeId: "",
  };
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const addLine = () => setLines((p) => [...p, { ...emptyLine }]);
  const removeLine = (i) => setLines((p) => p.filter((_, idx) => idx !== i));
  const updateLine = (i, field, value) =>
    setLines((p) =>
      p.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)),
    );

  const totalExcl = lines.reduce(
    (s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.unitPriceExcl) || 0),
    0,
  );
  const totalIncl = lines.reduce((s, l) => {
    const excl = (parseFloat(l.qty) || 0) * (parseFloat(l.unitPriceExcl) || 0);
    return s + excl * (1 + (parseFloat(l.vatRate) || 0) / 100);
  }, 0);

  const handleSubmit = async () => {
    setSaving(true);
    setErr(null);
    try {
      const userId = getLoggedInUserId();
      const vatRate = parseFloat(lines[0]?.vatRate) || 0;
      const body = {
        purchase_order_id: order?._id,
        document_number: `Q-${Date.now()}`,
        processed_by: userId,
        process_date: new Date().toISOString(),
        value_excl: totalExcl,
        value_incl: totalIncl,
        vat_rate: vatRate,
        status: 1,
        details: lines.map((l) => {
          const qty = parseFloat(l.qty) || 0,
            excl = parseFloat(l.unitPriceExcl) || 0;
          const vat = parseFloat(l.vatRate) || 0,
            unitIncl = excl * (1 + vat / 100);
          return {
            asset_type_id: l.assetTypeId || null,
            asset_type_code: l.assetCode || "",
            asset_description: l.product || "",
            qty_quoted: qty,
            unit_price_excl: excl,
            unit_price_incl: unitIncl,
            line_total_excl: qty * excl,
            line_total_incl: qty * unitIncl,
            vat_rate: vat,
          };
        }),
      };
      const result = await createQuote(body);
      toast?.("Quote created successfully", "s");
      onCreated?.(result);
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Failed to create quote");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.55)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "var(--deep)",
          border: "1px solid var(--bright)",
          borderTop: "2px solid var(--flame)",
          borderRadius: "var(--r8)",
          width: "100%",
          maxWidth: 820,
          maxHeight: "92vh",
          overflow: "auto",
          padding: 24,
          boxShadow: "0 10px 40px rgba(0,0,0,.45)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div>
            <div
              style={{ fontSize: 18, fontWeight: 800, color: "var(--text1)" }}
            >
              Create Quote
            </div>
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>
              Purchase Order: {order?.id || order?._id}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text3)",
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
        {err && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: "var(--r8)",
              background: "rgba(220,53,69,.1)",
              border: "1px solid rgba(220,53,69,.3)",
              color: "#e05260",
              fontSize: 12,
              marginBottom: 18,
            }}
          >
            ⚠️ {err}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {lines.map((l, i) => {
            const qty = parseFloat(l.qty) || 0,
              excl = parseFloat(l.unitPriceExcl) || 0;
            const vat = parseFloat(l.vatRate) || 0,
              unitIncl = excl * (1 + vat / 100);
            return (
              <div
                key={i}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: "var(--r8)",
                  background: "rgba(255,255,255,.02)",
                  padding: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "var(--text1)",
                    }}
                  >
                    Line Item #{i + 1}
                  </div>
                  <button
                    onClick={() => removeLine(i)}
                    disabled={lines.length === 1}
                    style={{
                      background: "transparent",
                      border: "1px solid rgba(220,53,69,.3)",
                      borderRadius: "var(--r8)",
                      color: "#e05260",
                      padding: "5px 10px",
                      cursor: lines.length === 1 ? "not-allowed" : "pointer",
                      opacity: lines.length === 1 ? 0.4 : 1,
                      fontSize: 11,
                    }}
                  >
                    Remove
                  </button>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
                    gap: 12,
                  }}
                >
                  <input
                    type="text"
                    placeholder="Asset Code"
                    value={l.assetCode}
                    onChange={(e) => updateLine(i, "assetCode", e.target.value)}
                    style={inputStyle}
                  />
                  <input
                    type="text"
                    placeholder="Description"
                    value={l.product}
                    onChange={(e) => updateLine(i, "product", e.target.value)}
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    placeholder="Quantity"
                    value={l.qty}
                    onChange={(e) => updateLine(i, "qty", e.target.value)}
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    placeholder="Unit Price Excl"
                    value={l.unitPriceExcl}
                    onChange={(e) =>
                      updateLine(i, "unitPriceExcl", e.target.value)
                    }
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    placeholder="VAT %"
                    value={l.vatRate}
                    onChange={(e) => updateLine(i, "vatRate", e.target.value)}
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    placeholder="Asset Type ID"
                    value={l.assetTypeId}
                    onChange={(e) =>
                      updateLine(i, "assetTypeId", e.target.value)
                    }
                    style={inputStyle}
                  />
                </div>
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: "1px solid var(--border)",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 16,
                    fontSize: 11,
                    color: "var(--text3)",
                  }}
                >
                  <div>
                    Unit Incl:{" "}
                    <strong style={{ color: "var(--text1)" }}>
                      {fmt.currency(unitIncl)}
                    </strong>
                  </div>
                  <div>
                    Line Excl:{" "}
                    <strong style={{ color: "var(--text1)" }}>
                      {fmt.currency(qty * excl)}
                    </strong>
                  </div>
                  <div>
                    Line Incl:{" "}
                    <strong style={{ color: "var(--text1)" }}>
                      {fmt.currency(qty * unitIncl)}
                    </strong>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <button
          onClick={addLine}
          style={{
            marginTop: 14,
            width: "100%",
            padding: 12,
            borderRadius: "var(--r8)",
            border: "1px dashed var(--bright)",
            background: "transparent",
            color: "var(--text2)",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          + Add Quote Line
        </button>
        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: "1px solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 14,
          }}
        >
          <div>
            <div
              style={{ fontSize: 10, color: "var(--text3)", marginBottom: 4 }}
            >
              Quote Totals
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: "var(--text1)",
                fontFamily: "monospace",
              }}
            >
              {fmt.currency(totalIncl)}
            </div>
            <div style={{ fontSize: 10, color: "var(--text3)" }}>
              excl VAT: {fmt.currency(totalExcl)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} className="btn btn-g">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="btn btn-p"
              style={{ opacity: saving ? 0.7 : 1 }}
            >
              {saving ? "Saving…" : "Create Quote"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── CreateInvoiceModal ───────────────────────────────────────────────────────
function CreateInvoiceModal({
  quote,
  order,
  onClose,
  onCreated,
  showToast: toast,
}) {
  const [sageInvNum, setSageInvNum] = useState(`INV-${Date.now()}`);
  const [sageDeliveryNum, setSageDeliveryNum] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [lines, setLines] = useState(
    (quote?.lines ?? []).map((l) => ({
      assetTypeId: l.assetTypeId ?? "",
      assetCode: l.assetCode ?? "",
      product: l.product ?? "",
      qty: l.qty ?? 1,
      unitPriceExcl: l.unitPriceExcl ?? "",
      vatRate: l.vatRate ?? 15,
    })),
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const addLine = () =>
    setLines((p) => [
      ...p,
      {
        assetTypeId: "",
        assetCode: "",
        product: "",
        qty: 1,
        unitPriceExcl: "",
        vatRate: 15,
      },
    ]);
  const removeLine = (i) => setLines((p) => p.filter((_, idx) => idx !== i));
  const updateLine = (i, field, value) =>
    setLines((p) =>
      p.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)),
    );

  const totalExcl = lines.reduce(
    (s, l) => s + (parseFloat(l.qty) || 0) * (parseFloat(l.unitPriceExcl) || 0),
    0,
  );
  const totalIncl = lines.reduce((s, l) => {
    const excl = (parseFloat(l.qty) || 0) * (parseFloat(l.unitPriceExcl) || 0);
    return s + excl * (1 + (parseFloat(l.vatRate) || 0) / 100);
  }, 0);

  const handleSubmit = async () => {
    setSaving(true);
    setErr(null);
    try {
      const userId = getLoggedInUserId();
      const vatRate = parseFloat(lines[0]?.vatRate) || 0;
      const body = {
        quotation_id: quote.id,
        sage_invoice_number: sageInvNum,
        sage_delivery_note_number: sageDeliveryNum || undefined,
        processed_by: userId,
        invoice_date: invoiceDate,
        value_excl: totalExcl,
        value_incl: totalIncl,
        vat_rate: vatRate,
        details: lines.map((l) => {
          const qty = parseFloat(l.qty) || 0,
            excl = parseFloat(l.unitPriceExcl) || 0;
          const vat = parseFloat(l.vatRate) || 0,
            unitIncl = excl * (1 + vat / 100);
          return {
            asset_type_id: l.assetTypeId || null,
            asset_type_code: l.assetCode || "",
            asset_type_description: l.product || "",
            quantity: qty,
            unit_price_excl: excl,
            unit_price_incl: unitIncl,
            line_total_excl: qty * excl,
            line_total_incl: qty * unitIncl,
            vat_rate: vat,
          };
        }),
      };
      const result = await createInvoice(body);
      toast?.("Invoice created successfully", "s");
      onCreated?.(result);
      onClose?.();
    } catch (e) {
      setErr(e?.message || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.55)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "var(--deep)",
          border: "1px solid var(--bright)",
          borderTop: "2px solid #198754",
          borderRadius: "var(--r8)",
          width: "100%",
          maxWidth: 860,
          maxHeight: "92vh",
          overflow: "auto",
          padding: 24,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div>
            <div
              style={{ fontSize: 18, fontWeight: 800, color: "var(--text1)" }}
            >
              Create Invoice
            </div>
            <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>
              Quote: {quote?.documentNumber ?? quote?.id} · PO: {order?.id}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text3)",
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>
        {err && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: "var(--r8)",
              background: "rgba(220,53,69,.1)",
              border: "1px solid rgba(220,53,69,.3)",
              color: "#e05260",
              fontSize: 12,
              marginBottom: 18,
            }}
          >
            ⚠️ {err}
          </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div>
            <div
              style={{ fontSize: 10, color: "var(--text3)", marginBottom: 5 }}
            >
              Sage Invoice Number *
            </div>
            <input
              type="text"
              value={sageInvNum}
              onChange={(e) => setSageInvNum(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <div
              style={{ fontSize: 10, color: "var(--text3)", marginBottom: 5 }}
            >
              Sage Delivery Note Number
            </div>
            <input
              type="text"
              value={sageDeliveryNum}
              onChange={(e) => setSageDeliveryNum(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <div
              style={{ fontSize: 10, color: "var(--text3)", marginBottom: 5 }}
            >
              Invoice Date *
            </div>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {lines.map((l, i) => {
            const qty = parseFloat(l.qty) || 0,
              excl = parseFloat(l.unitPriceExcl) || 0;
            const vat = parseFloat(l.vatRate) || 0,
              unitIncl = excl * (1 + vat / 100);
            return (
              <div
                key={i}
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: "var(--r8)",
                  background: "rgba(255,255,255,.02)",
                  padding: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "var(--text1)",
                    }}
                  >
                    Line Item #{i + 1}
                  </div>
                  <button
                    onClick={() => removeLine(i)}
                    disabled={lines.length === 1}
                    style={{
                      background: "transparent",
                      border: "1px solid rgba(220,53,69,.3)",
                      borderRadius: "var(--r8)",
                      color: "#e05260",
                      padding: "5px 10px",
                      cursor: lines.length === 1 ? "not-allowed" : "pointer",
                      opacity: lines.length === 1 ? 0.4 : 1,
                      fontSize: 11,
                    }}
                  >
                    Remove
                  </button>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
                    gap: 12,
                  }}
                >
                  <input
                    type="text"
                    placeholder="Asset Code"
                    value={l.assetCode}
                    onChange={(e) => updateLine(i, "assetCode", e.target.value)}
                    style={inputStyle}
                  />
                  <input
                    type="text"
                    placeholder="Description"
                    value={l.product}
                    onChange={(e) => updateLine(i, "product", e.target.value)}
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    placeholder="Quantity"
                    value={l.qty}
                    onChange={(e) => updateLine(i, "qty", e.target.value)}
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    placeholder="Unit Price Excl"
                    value={l.unitPriceExcl}
                    onChange={(e) =>
                      updateLine(i, "unitPriceExcl", e.target.value)
                    }
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    placeholder="VAT %"
                    value={l.vatRate}
                    onChange={(e) => updateLine(i, "vatRate", e.target.value)}
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    placeholder="Asset Type ID"
                    value={l.assetTypeId}
                    onChange={(e) =>
                      updateLine(i, "assetTypeId", e.target.value)
                    }
                    style={inputStyle}
                  />
                </div>
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: "1px solid var(--border)",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 16,
                    fontSize: 11,
                    color: "var(--text3)",
                  }}
                >
                  <div>
                    Unit Incl:{" "}
                    <strong style={{ color: "var(--text1)" }}>
                      {fmt.currency(unitIncl)}
                    </strong>
                  </div>
                  <div>
                    Line Excl:{" "}
                    <strong style={{ color: "var(--text1)" }}>
                      {fmt.currency(qty * excl)}
                    </strong>
                  </div>
                  <div>
                    Line Incl:{" "}
                    <strong style={{ color: "var(--text1)" }}>
                      {fmt.currency(qty * unitIncl)}
                    </strong>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <button
          onClick={addLine}
          style={{
            marginTop: 14,
            width: "100%",
            padding: 12,
            borderRadius: "var(--r8)",
            border: "1px dashed var(--bright)",
            background: "transparent",
            color: "var(--text2)",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          + Add Invoice Line
        </button>
        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: "1px solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 14,
          }}
        >
          <div>
            <div
              style={{ fontSize: 10, color: "var(--text3)", marginBottom: 4 }}
            >
              Invoice Totals
            </div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: "var(--text1)",
                fontFamily: "monospace",
              }}
            >
              {fmt.currency(totalIncl)}
            </div>
            <div style={{ fontSize: 10, color: "var(--text3)" }}>
              excl VAT: {fmt.currency(totalExcl)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} className="btn btn-g">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="btn btn-success"
              style={{ opacity: saving ? 0.7 : 1 }}
            >
              {saving ? "Saving…" : "Create Invoice"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── QuoteSection ─────────────────────────────────────────────────────────────
function QuoteSection({
  quote,
  order,
  isSupplier,
  onCreateQuote,
  onQuoteDecision,
  onInvoiceCreated,
  toast,
}) {
  const [showCreateQuote, setShowCreateQuote] = useState(false);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [localQuote, setLocalQuote] = useState(quote);
  const [reloading, setReloading] = useState(false);

  const { canCreateQuotations, canCreateInvoices } = usePermissions();

  useEffect(() => {
    setLocalQuote(quote);
  }, [quote]);

  const reloadQuote = useCallback(async () => {
    if (!localQuote?.id) return;
    setReloading(true);
    try {
      const raw = await getQuoteById(localQuote.id);
      const refreshed = normaliseQuote(raw);
      if (refreshed) {
        setLocalQuote(refreshed);
        onQuoteDecision?.(refreshed.statusCode, refreshed);
      }
    } catch {
      /* silently ignore */
    } finally {
      setReloading(false);
    }
  }, [localQuote?.id, onQuoteDecision]);

  const quoteAccepted = ["approved", "accepted"].includes(
    (localQuote?.statusCode ?? localQuote?.status ?? "").toLowerCase(),
  );

  return (
    <>
      {showCreateQuote && (
        <CreateQuoteModal
          order={order}
          onClose={() => setShowCreateQuote(false)}
          onCreated={(raw) => {
            const q = normaliseQuote(raw);
            setLocalQuote(q);
            onCreateQuote?.(q);
          }}
          showToast={toast}
        />
      )}
      {showCreateInvoice && localQuote && (
        <CreateInvoiceModal
          quote={localQuote}
          order={order}
          onClose={() => setShowCreateInvoice(false)}
          onCreated={(raw) => {
            onInvoiceCreated?.(normaliseInvoice(raw));
          }}
          showToast={toast}
        />
      )}
      <div className="panel mb-16">
        <div
          className="ph"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span className="ph-title">
            {isSupplier ? "Quote Sent to Customer" : "Quote from Supplier"}
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {reloading && (
              <span style={{ fontSize: 11, color: "var(--text3)" }}>
                Refreshing…
              </span>
            )}
            {localQuote && (
              <DownloadPdfButton
                quotationId={localQuote.id}
                filename={`quote-${localQuote.documentNumber ?? localQuote.id}.pdf`}
                style={{ ...btn("ghost"), fontSize: 11 }}
                toast={toast}
              />
            )}
            {isSupplier && !localQuote && canCreateQuotations && (
              <button
                onClick={() => setShowCreateQuote(true)}
                className="btn btn-outline-flame"
              >
                <i className="bi bi-file-earmark-plus" /> Create Quote
              </button>
            )}
            {isSupplier && localQuote && quoteAccepted && canCreateInvoices && (
              <button
                onClick={() => setShowCreateInvoice(true)}
                className="btn btn-success"
              >
                <i className="bi bi-receipt" /> Create Invoice
              </button>
            )}
          </div>
        </div>
        <div
          className="pb"
          style={{ padding: localQuote?.lines?.length ? 0 : undefined }}
        >
          {!localQuote ? (
            <div className="empty" style={{ padding: "16px 0" }}>
              {isSupplier
                ? "No quote created for this order yet."
                : "The supplier hasn't sent a quote yet."}
            </div>
          ) : (
            <>
              <div
                style={{
                  padding: "14px 16px",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                {!isSupplier && (
                  <QuoteDecisionBanner
                    quote={localQuote}
                    toast={toast}
                    onDecision={() => {
                      reloadQuote();
                    }}
                  />
                )}
                {isSupplier && quoteAccepted && (
                  <div
                    style={{
                      padding: "10px 14px",
                      background: "rgba(25,135,84,.08)",
                      border: "1px solid rgba(25,135,84,.3)",
                      borderRadius: "var(--r8)",
                      marginBottom: 14,
                      fontSize: 12,
                      color: "#4caf86",
                      fontWeight: 600,
                    }}
                  >
                    Customer accepted this quote. You can now create the final
                    invoice.
                  </div>
                )}
                <div className="review-block">
                  {[
                    ["Quotation ID", localQuote.id],
                    ["Document", localQuote.documentNumber],
                    ["Purchase Order", localQuote.purchaseOrderId],
                    ["Status", localQuote.status],
                    ["Status ID", localQuote.statusId],
                    [
                      "Process Date",
                      localQuote.processDate
                        ? fmt.date(localQuote.processDate)
                        : null,
                    ],
                    [
                      "Date Approved",
                      localQuote.dateApproved
                        ? fmt.date(localQuote.dateApproved)
                        : null,
                    ],
                    ["Processed By", localQuote.processedBy],
                    ["Processed By Email", localQuote.processedByEmail],
                    ["Processed By Phone", localQuote.processedByPhone],
                    ["Approved By", localQuote.approvedBy],
                    ["Approved By Email", localQuote.approvedByEmail],
                    ["Approved By Phone", localQuote.approvedByPhone],
                    [
                      "VAT Rate",
                      localQuote.vatRate != null
                        ? `${localQuote.vatRate}%`
                        : null,
                    ],
                    [
                      "Value Excl. VAT",
                      localQuote.valueExcl != null
                        ? fmt.currency(localQuote.valueExcl)
                        : null,
                    ],
                    [
                      "Value Incl. VAT",
                      localQuote.valueIncl != null
                        ? fmt.currency(localQuote.valueIncl)
                        : null,
                    ],
                    ["Invoice URL", localQuote.invoiceUrl],
                    ["Notes", localQuote.notes],
                  ]
                    .filter(([, v]) => v != null && v !== "")
                    .map(([k, v]) => (
                      <div key={k} className="review-row">
                        <span className="review-label">{k}</span>
                        <span className="review-value">{v}</span>
                      </div>
                    ))}
                </div>
              </div>

              {localQuote.purchaseOrder && (
                <div
                  style={{
                    padding: "14px 16px",
                    borderBottom: "1px solid var(--border)",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: 14,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "var(--text3)",
                        marginBottom: 6,
                        textTransform: "uppercase",
                        letterSpacing: ".04em",
                      }}
                    >
                      Linked Purchase Order
                    </div>
                    <div className="review-block">
                      {[
                        ["PO Number", localQuote.purchaseOrder.number],
                        [
                          "Order Date",
                          localQuote.purchaseOrder.orderDate
                            ? fmt.date(localQuote.purchaseOrder.orderDate)
                            : null,
                        ],
                        [
                          "Required By",
                          localQuote.purchaseOrder.requiredDeliveryDate
                            ? fmt.date(
                                localQuote.purchaseOrder.requiredDeliveryDate,
                              )
                            : null,
                        ],
                      ]
                        .filter(([, v]) => v != null && v !== "")
                        .map(([k, v]) => (
                          <div key={k} className="review-row">
                            <span className="review-label">{k}</span>
                            <span className="review-value">{v}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                  {localQuote.purchaseOrder.warehouse && (
                    <div
                      style={{
                        borderLeft: "1px solid var(--border)",
                        paddingLeft: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "var(--text3)",
                          marginBottom: 6,
                          textTransform: "uppercase",
                          letterSpacing: ".04em",
                        }}
                      >
                        Warehouse
                      </div>
                      <div className="review-block">
                        {[
                          ["Name", localQuote.purchaseOrder.warehouse.name],
                          ...addressRows(
                            localQuote.purchaseOrder.warehouse.address,
                          ),
                        ]
                          .filter(([, v]) => v != null && v !== "")
                          .map(([k, v]) => (
                            <div key={k} className="review-row">
                              <span className="review-label">{k}</span>
                              <span className="review-value">{v}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                  {localQuote.purchaseOrder.customerBranch && (
                    <div
                      style={{
                        borderLeft: "1px solid var(--border)",
                        paddingLeft: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "var(--text3)",
                          marginBottom: 6,
                          textTransform: "uppercase",
                          letterSpacing: ".04em",
                        }}
                      >
                        Customer Branch
                      </div>
                      <div className="review-block">
                        {[
                          [
                            "Branch",
                            localQuote.purchaseOrder.customerBranch.name,
                          ],
                          [
                            "Organization",
                            localQuote.purchaseOrder.customerBranch
                              .organization,
                          ],
                          [
                            "VAT Reg",
                            localQuote.purchaseOrder.customerBranch
                              .vatRegistration,
                          ],
                          [
                            "Company Reg",
                            localQuote.purchaseOrder.customerBranch
                              .companyRegistration,
                          ],
                          ...addressRows(
                            localQuote.purchaseOrder.customerBranch.address,
                          ),
                        ]
                          .filter(([, v]) => v != null && v !== "")
                          .map(([k, v]) => (
                            <div key={k} className="review-row">
                              <span className="review-label">{k}</span>
                              <span className="review-value">{v}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                  {localQuote.purchaseOrder.supplierBranch && (
                    <div
                      style={{
                        borderLeft: "1px solid var(--border)",
                        paddingLeft: 14,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "var(--text3)",
                          marginBottom: 6,
                          textTransform: "uppercase",
                          letterSpacing: ".04em",
                        }}
                      >
                        Supplier Branch
                      </div>
                      <div className="review-block">
                        {[
                          [
                            "Branch",
                            localQuote.purchaseOrder.supplierBranch.name,
                          ],
                          [
                            "Organization",
                            localQuote.purchaseOrder.supplierBranch
                              .organization,
                          ],
                          [
                            "VAT Reg",
                            localQuote.purchaseOrder.supplierBranch
                              .vatRegistration,
                          ],
                          [
                            "Company Reg",
                            localQuote.purchaseOrder.supplierBranch
                              .companyRegistration,
                          ],
                          ...addressRows(
                            localQuote.purchaseOrder.supplierBranch.address,
                          ),
                        ]
                          .filter(([, v]) => v != null && v !== "")
                          .map(([k, v]) => (
                            <div key={k} className="review-row">
                              <span className="review-label">{k}</span>
                              <span className="review-value">{v}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {localQuote.updates?.length > 0 && (
                <div
                  style={{
                    padding: "0 16px 14px",
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--text3)",
                      margin: "14px 0 8px",
                      textTransform: "uppercase",
                      letterSpacing: ".04em",
                    }}
                  >
                    Quotation History ({localQuote.updates.length})
                  </div>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Update ID</th>
                        <th>Status ID</th>
                        <th>Notes</th>
                        <th>User ID</th>
                        <th>Action Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localQuote.updates.map((u, i) => (
                        <tr key={u.quotation_update_id ?? i}>
                          <td className="mono fs10 tc3">
                            {u.quotation_update_id ?? "—"}
                          </td>
                          <td className="mono fs10 tc3">
                            {u.quotation_status_id ?? "—"}
                          </td>
                          <td>{u.notes ?? "—"}</td>
                          <td className="mono fs10 tc3">{u.user_id ?? "—"}</td>
                          <td className="mono fs10 tc3">
                            {u.action_date ? fmt.date(u.action_date) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {localQuote.lines?.length > 0 && (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Description</th>
                      <th style={{ textAlign: "right" }}>Qty</th>
                      <th style={{ textAlign: "right" }}>Unit (excl)</th>
                      <th style={{ textAlign: "right" }}>Unit (incl)</th>
                      <th style={{ textAlign: "right" }}>Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {localQuote.lines.map((l, i) => (
                      <tr key={l.id ?? i}>
                        <td className="mono fs10 tc3">{l.assetCode ?? "—"}</td>
                        <td>{l.product ?? "—"}</td>
                        <td className="mono" style={{ textAlign: "right" }}>
                          {l.qty ?? "—"}
                        </td>
                        <td className="mono" style={{ textAlign: "right" }}>
                          {fmt.currency(l.unitPriceExcl)}
                        </td>
                        <td className="mono" style={{ textAlign: "right" }}>
                          {fmt.currency(l.unitPriceIncl)}
                        </td>
                        <td
                          className="mono fw-6"
                          style={{ textAlign: "right" }}
                        >
                          {fmt.currency(l.lineTotalIncl)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <LineItemsTotals
                valueExcl={localQuote.valueExcl}
                valueIncl={localQuote.valueIncl}
                vatRate={localQuote.vatRate}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

// ─── InvoiceSection ───────────────────────────────────────────────────────────
function InvoiceSection({ invoices, toast }) {
  const { canViewInvoices } = usePermissions();
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  if (!canViewInvoices || !invoices) return null;

  return (
    <div className="panel mb-16">
      <div className="ph">
        <span className="ph-title">Invoices ({invoices.length})</span>
      </div>
      <div className="pb" style={{ padding: invoices.length ? 0 : undefined }}>
        {invoices.length === 0 ? (
          <div className="empty" style={{ padding: "16px 0" }}>
            No invoices for this order yet.
          </div>
        ) : (
          <>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Invoice ID</th>
                  <th>Sage Invoice #</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Excl. VAT</th>
                  <th style={{ textAlign: "right" }}>Incl. VAT</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) => {
                  const isSelected = selectedInvoice?.id === inv.id;
                  return (
                    <tr
                      key={inv.id ?? i}
                      onClick={() =>
                        setSelectedInvoice(isSelected ? null : inv)
                      }
                      style={{
                        background: isSelected
                          ? "rgba(255,79,23,.05)"
                          : undefined,
                        cursor: "pointer",
                        outline: isSelected
                          ? "1px solid rgba(255,79,23,.2)"
                          : "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected)
                          e.currentTarget.style.background =
                            "rgba(255,255,255,.03)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = isSelected
                          ? "rgba(255,79,23,.05)"
                          : "";
                      }}
                    >
                      <td className="mono t-flame">{inv.id ?? "—"}</td>
                      <td className="mono fs10 tc3">
                        {inv.sageInvoiceNumber ?? "—"}
                      </td>
                      <td className="mono fs10 tc3">
                        {inv.invoiceDate ? fmt.date(inv.invoiceDate) : "—"}
                      </td>
                      <td>
                        <QuoteBadge status={inv.status ?? "—"} />
                      </td>
                      <td className="mono" style={{ textAlign: "right" }}>
                        {fmt.currency(inv.valueExcl)}
                      </td>
                      <td className="mono fw-6" style={{ textAlign: "right" }}>
                        {fmt.currency(inv.valueIncl)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {selectedInvoice && (
              <div style={{ padding: "0 16px 16px" }}>
                <InvoiceDetailPanel
                  invoice={selectedInvoice}
                  onClose={() => setSelectedInvoice(null)}
                  toast={toast}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── OrderDetailPage ──────────────────────────────────────────────────────────
function OrderDetailPage({ order, onBack, showToast: toast, setPage }) {
  const [details, setDetails] = useState(null);
  const [quote, setQuote] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const isSupplier = useRole() === "supplier";

  const { canCancelOrders, canViewQuotations, canViewInvoices, ready } =
    usePermissions();

  // Permission AND status-id checks both gate the Cancel action: the order
  // must still be in its initial "New" state (statusId === 1).
  const canCancel = ready && canCancelOrders && isOrderCancellable(order);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [det, rawQuoteList, inv] = await Promise.allSettled([
          getOrderDetail(order._id),
          getQuoteByPurchaseOrder(order._id),
          getInvoicesByPurchaseOrder(order._id),
        ]);
        if (!cancelled) {
          if (det.status === "fulfilled") setDetails(det.value);
          if (inv.status === "fulfilled") {
            const raw = inv.value;
            const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
            setInvoices(list.map(normaliseInvoice).filter(Boolean));
          }
        }

        // ── Quote data source fix ──────────────────────────────────────
        if (!cancelled && rawQuoteList.status === "fulfilled") {
          const list = rawQuoteList.value;
          if (list.length > 0) {
            const approved = list.find(
              (q) => q.quotation_status?.quotation_status_id === 2,
            );
            const chosen = approved ?? list[list.length - 1];
            try {
              const fullQuote = await getQuoteById(chosen.quotation_id);
              if (!cancelled) setQuote(normaliseQuote(fullQuote));
            } catch {
              if (!cancelled) setQuote(normaliseQuote(chosen));
            }
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order._id, reloadKey]);

  const handleCancel = async () => {
    if (!window.confirm("Cancel this purchase order? This cannot be undone."))
      return;
    setCancelling(true);
    try {
      await cancelOrder(order._id);
      toast(`${order.id} cancelled`, "s");
      onBack();
    } catch (e) {
      toast(`Failed to cancel: ${e.message}`, "e");
    } finally {
      setCancelling(false);
    }
  };

  if (loading)
    return (
      <div className="page">
        <div
          className="panel"
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--text3)",
            fontSize: 13,
          }}
        >
          <span
            style={{
              animation: "spin 1s linear infinite",
              display: "inline-block",
              marginRight: 8,
            }}
          >
            ⟳
          </span>
          Loading order details…
        </div>
      </div>
    );

  if (error)
    return (
      <div className="page">
        <button className="back-btn" onClick={onBack}>
          ← Back to Orders
        </button>
        <ErrorState
          message={error}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </div>
    );

  const d = details ?? order._raw ?? {};

  return (
    <div className="page action-page" style={{ maxWidth: 1600 }}>
      <div className="mb-22">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {canCancel && (
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="btn btn-d"
                style={{ opacity: cancelling ? 0.6 : 1 }}
              >
                <i className="bi bi-x-circle" />{" "}
                {cancelling ? "Cancelling…" : "Cancel Order"}
              </button>
            )}
          </div>
        </div>

        <button className="back-btn" onClick={onBack}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            color="#99E4D5"
          >
            <path d="M19 12H5" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Orders
        </button>
      </div>

      <div
        className="equal-row"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div className="panel">
          <div className="ph">
            <span className="ph-title">Order Information</span>
          </div>
          <div className="pb">
            <div className="review-block">
              {[
                ["Order Number", d.purchase_order_number ?? order.id],
                ["Status", d.status?.description ?? order.status],
                ["Order Date", d.order_date ? fmt.date(d.order_date) : "—"],
                [
                  "Required By",
                  d.required_delivery_date
                    ? fmt.date(d.required_delivery_date)
                    : "—",
                ],
                ["Notes", d.notes || "—"],
                ["Created", d.creation_date ? fmt.date(d.creation_date) : "—"],
              ].map(([k, v]) => (
                <div key={k} className="review-row">
                  <span className="review-label">{k}</span>
                  <span className="review-value">{v ?? "—"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="panel">
          <div className="ph">
            <span className="ph-title">Delivery Address</span>
          </div>
          <div className="pb">
            <div className="review-block">
              {[
                ["Address Line 1", d.address_line_1],
                ["Address Line 2", d.address_line_2],
                ["City", d.city],
                ["Region", d.region],
                ["Postal Code", d.postal_code],
                ["Country", d.country],
                ["Geo Location", d.geo_location],
              ]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={k} className="review-row">
                    <span className="review-label">{k}</span>
                    <span className="review-value">{v}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      <div
        className="equal-row"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginBottom: 16,
        }}
      >
        {d.supplier_branch && (
          <div className="panel">
            <div className="ph">
              <span className="ph-title">Supplier Branch</span>
            </div>
            <div className="pb">
              <div className="review-block">
                {[
                  ["Branch", d.supplier_branch.branch_name],
                  [
                    "Organization",
                    d.supplier_branch.organization?.name ??
                      d.supplier_branch.organization?.organization_name,
                  ],
                  ["VAT Reg", d.supplier_branch.vat_registration],
                  ["Company Reg", d.supplier_branch.company_registration],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="review-row">
                      <span className="review-label">{k}</span>
                      <span className="review-value">{v}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
        {d.customer_branch && (
          <div className="panel">
            <div className="ph">
              <span className="ph-title">Customer Branch</span>
            </div>
            <div className="pb">
              <div className="review-block">
                {[
                  ["Branch", d.customer_branch.branch_name],
                  [
                    "Organization",
                    d.customer_branch.organization?.name ??
                      d.customer_branch.organization?.organization_name,
                  ],
                  ["VAT Reg", d.customer_branch.vat_registration],
                  ["Company Reg", d.customer_branch.company_registration],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="review-row">
                      <span className="review-label">{k}</span>
                      <span className="review-value">{v}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
        {d.warehouse && (
          <div className="panel">
            <div className="ph">
              <span className="ph-title">Warehouse</span>
            </div>
            <div className="pb">
              <div className="review-block">
                {[
                  ["Name", d.warehouse.warehouse_name],
                  [
                    "Type",
                    d.warehouse.warehouse_type?.description ??
                      d.warehouse.warehouse_type?.warehouse_type_name,
                  ],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="review-row">
                      <span className="review-label">{k}</span>
                      <span className="review-value">{v}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
        {d.created_by && (
          <div className="panel">
            <div className="ph">
              <span className="ph-title">Created By</span>
            </div>
            <div className="pb">
              <div className="review-block">
                {[
                  [
                    "Name",
                    `${d.created_by.name} ${d.created_by.surname}`.trim(),
                  ],
                  ["Email", d.created_by.email_address],
                  ["Phone", d.created_by.contact_number],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="review-row">
                      <span className="review-label">{k}</span>
                      <span className="review-value">{v}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {Array.isArray(d.details) && d.details.length > 0 && (
        <div className="panel mb-16">
          <div className="ph">
            <span className="ph-title">Order Lines ({d.details.length})</span>
          </div>
          <div className="pb" style={{ padding: 0 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th style={{ textAlign: "right" }}>Qty Ordered</th>
                </tr>
              </thead>
              <tbody>
                {d.details.map((line, i) => (
                  <tr key={line.purchase_order_detail_id ?? line.pod_id ?? i}>
                    <td className="mono fs10 tc3">
                      {line.asset_type_code ?? "—"}
                    </td>
                    <td>{line.asset_type_description ?? "—"}</td>
                    <td className="mono" style={{ textAlign: "right" }}>
                      {line.qty_ordered ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {canViewQuotations && (
        <QuoteSection
          quote={quote}
          order={order}
          isSupplier={isSupplier}
          onCreateQuote={setQuote}
          onQuoteDecision={(status, updatedQuote) => setQuote(updatedQuote)}
          onInvoiceCreated={(inv) => setInvoices((prev) => [inv, ...prev])}
          toast={toast}
        />
      )}

      {canViewInvoices && <InvoiceSection invoices={invoices} toast={toast} />}
    </div>
  );
}

// ─── Main OrdersPage ──────────────────────────────────────────────────────────
export default function OrdersPage({ showToast: toast, setPage }) {
  const [devRole, setDevRole] = useState(null);
  const roleCtxValue = { role: devRole, setRole: setDevRole };
  return (
    <RoleOverrideContext.Provider value={roleCtxValue}>
      <OrdersPageInner showToast={toast} setPage={setPage} />
    </RoleOverrideContext.Provider>
  );
}

function OrdersPageInner({ showToast: toast, setPage }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [showCreateQuoteFor, setShowCreateQuoteFor] = useState(null);
  const [activePage, setActivePage] = useState("list");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const isSupplier = useRole() === "supplier";

  const navigateTo = (page, order) => {
    setSelectedOrder(order);
    setActivePage(page);
  };
  const goBack = () => {
    setActivePage("list");
    setSelectedOrder(null);
  };

  useEffect(() => {
    let cancelled = false;
    const scope = getUserScope();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        let raw;
        if (scope.type === "branch") raw = await getOrdersByBranch(scope.id);
        else if (scope.type === "warehouse")
          raw = await getOrdersByWarehouse(scope.id);
        else raw = await fetchOrders();
        if (!cancelled)
          setOrders((Array.isArray(raw) ? raw : []).map(normaliseOrder));
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const updateOrderStatus = useCallback(
    (order, newStatus) => {
      setOrders((p) =>
        p.map((o) => (o.id === order.id ? { ...o, status: newStatus } : o)),
      );
      toast(`${order.id} → ${newStatus}`, "s");
    },
    [toast],
  );

  const handleCancelOrder = useCallback(
    async (order) => {
      if (!window.confirm(`Cancel order ${order.id}? This cannot be undone.`))
        return;
      try {
        await cancelOrder(order._id);
        updateOrderStatus(order, "Cancelled");
        toast(`${order.id} cancelled`, "s");
      } catch (e) {
        toast(`Failed to cancel ${order.id}: ${e.message}`, "e");
      }
    },
    [updateOrderStatus, toast],
  );

  const toggleExpand = (orderId) =>
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));

  const {
    canCreateOrders,
    canCancelOrders,
    canProcessOrders,
    canViewQuotations,
    canViewInvoices,
    canSeePage,
    ready,
  } = usePermissions();

  const canCreate = ready && canCreateOrders;
  const canCancel = ready && canCancelOrders;
  const canProcess = ready && canProcessOrders;

  if (ready && !canSeePage("orders")) return null;

  if (activePage === "detail" && selectedOrder)
    return (
      <OrderDetailPage
        order={selectedOrder}
        onBack={goBack}
        showToast={toast}
        setPage={setPage}
      />
    );
  if (activePage === "quote" && selectedOrder)
    return (
      <QuoteViewPage order={selectedOrder} onBack={goBack} showToast={toast} />
    );
  if (activePage === "invoice" && selectedOrder)
    return (
      <InvoiceViewPage
        order={selectedOrder}
        onBack={goBack}
        showToast={toast}
      />
    );

  const filtered = orders.filter((o) => {
    const q = search.toLowerCase();
    const matchQ =
      o.id.toLowerCase().includes(q) ||
      o.site.toLowerCase().includes(q) ||
      (o.notes ?? "").toLowerCase().includes(q);
    return matchQ && (filterStatus === "All" || o.status === filterStatus);
  });

  if (loading)
    return (
      <div className="page">
        <div
          className="panel"
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--text3)",
            fontSize: 13,
          }}
        >
          <span
            style={{
              animation: "spin 1s linear infinite",
              display: "inline-block",
              marginRight: 8,
            }}
          >
            ⟳
          </span>
          Loading orders…
        </div>
      </div>
    );

  if (error)
    return (
      <div className="page">
        <ErrorState
          message={error}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </div>
    );

  return (
    <div className="page">
      {showCreateQuoteFor && (
        <CreateQuoteModal
          order={showCreateQuoteFor}
          onClose={() => setShowCreateQuoteFor(null)}
          onCreated={() => {
            setExpandedOrderId(showCreateQuoteFor.id);
            setShowCreateQuoteFor(null);
          }}
          showToast={toast}
        />
      )}
      <div className="panel">
        <div className="tbar">
          <div className="tsearch">
            <i
              className="bi bi-search"
              style={{ color: "var(--text3)", fontSize: 12 }}
            />
            <input
              placeholder="Search by ID, site, notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="tfilter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            {["All", ...PIPELINE].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          {canCreate && (
            <button
              className="btn btn-info"
              onClick={() => setPage?.("create-order")}
            >
              <i className="bi bi-plus-lg" /> New Order
            </button>
          )}
          <span
            className="fs11 mono"
            style={{ marginLeft: "auto", color: "var(--text3)" }}
          >
            {filtered.length} of {orders.length} orders
          </span>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 40 }} />
              <th>Order ID</th>
              <th>Site</th>
              <th>Supplier</th>
              <th>Order Date</th>
              <th>Delivery</th>
              <th style={{ textAlign: "right" }}>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map((o) => {
                const isExpanded = expandedOrderId === o.id;
                return (
                  <>
                    <tr
                      key={o.id}
                      style={{
                        cursor: "pointer",
                        background: isExpanded
                          ? "rgba(255,79,23,.04)"
                          : undefined,
                        borderBottom: isExpanded ? "none" : undefined,
                      }}
                      onClick={() => toggleExpand(o.id)}
                    >
                      <td style={{ textAlign: "center", padding: "0 6px" }}>
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            border: `1.5px solid ${isExpanded ? "var(--flame)" : "var(--bright)"}`,
                            background: isExpanded
                              ? "rgba(255,79,23,.1)"
                              : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 9,
                            color: isExpanded ? "var(--flame)" : "var(--text3)",
                            transition: "all .15s",
                            userSelect: "none",
                          }}
                        >
                          {isExpanded ? "▲" : "▼"}
                        </div>
                      </td>
                      <td>
                        <span
                          className="mono t-flame"
                          style={{ fontWeight: 700, fontSize: 11 }}
                        >
                          {o.id}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 12 }}>
                          {o.site}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 12 }}>
                          {o.supplier}
                        </div>
                      </td>
                      <td
                        className="mono fs10"
                        style={{ color: "var(--text3)" }}
                      >
                        {o.date ? fmt.date(o.date) : "—"}
                      </td>
                      <td
                        className="mono fs10"
                        style={{ color: "var(--text3)" }}
                      >
                        {o.delivery ? fmt.date(o.delivery) : "—"}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Badge status={o.status} />
                      </td>
                      <td
                        style={{ textAlign: "center" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <ActionMenu
                            items={[
                              ...(canProcess
                                ? (STATUS_FLOW[o.status] || []).map((s) => ({
                                    icon: <i className="bi bi-arrow-repeat" />,
                                    label: `Move to ${s}`,
                                    action: () => updateOrderStatus(o, s),
                                  }))
                                : []),
                              ...(isSupplier
                                ? [
                                    {
                                      icon: (
                                        <i className="bi bi-file-earmark-plus" />
                                      ),
                                      label: "Create Quote",
                                      action: () => setShowCreateQuoteFor(o),
                                    },
                                  ]
                                : []),
                              ...(canCancel && isOrderCancellable(o)
                                ? [
                                    {
                                      icon: <i className="bi bi-x-circle" />,
                                      label: "Cancel Order",
                                      danger: true,
                                      action: () => handleCancelOrder(o),
                                    },
                                  ]
                                : []),
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={15}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: 16,
                              flexWrap: "wrap",
                              gap: 10,
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontWeight: 700,
                                  fontSize: 14,
                                  color: "var(--text1)",
                                  marginBottom: 2,
                                }}
                              >
                                {o.id}
                              </div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "var(--text3)",
                                }}
                              >
                                {o.notes || "No notes available"}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fit,minmax(160px,1fr))",
                              gap: 12,
                              marginBottom: 20,
                            }}
                          >
                            <div>
                              <div className="fs10 tc3">Site</div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>
                                {o.site}
                              </div>
                            </div>
                            <div>
                              <div className="fs10 tc3">Order Date</div>
                              <div>{o.date ? fmt.date(o.date) : "—"}</div>
                            </div>
                            <div>
                              <div className="fs10 tc3">Delivery</div>
                              <div>
                                {o.delivery ? fmt.date(o.delivery) : "—"}
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 10,
                              flexWrap: "wrap",
                              paddingTop: 16,
                              borderTop: "1px solid var(--border)",
                            }}
                          >
                            {canViewQuotations && (
                              <button
                                onClick={() => navigateTo("quote", o)}
                                className="btn btn-info"
                              >
                                <i className="bi bi-file-earmark-text" /> View
                                Quote
                              </button>
                            )}
                            {canViewInvoices && (
                              <button
                                onClick={() => navigateTo("invoice", o)}
                                className="btn btn-warning"
                              >
                                <i className="bi bi-receipt" /> View Invoice
                              </button>
                            )}
                            {canViewQuotations && canViewInvoices && (
                              <button
                                onClick={() => navigateTo("detail", o)}
                                className="btn btn-info"
                              >
                                <i className="bi bi-box-arrow-up-right" /> Full
                                Detail
                              </button>
                            )}
                            {/* Cancel Button important — only shown while
                                status id === 1 (New); permission check
                                (canCancel) still applies on top of this */}
                            {canCancel && isOrderCancellable(o) && (
                              <button
                                className="btn btn-info"
                                onClick={() => handleCancelOrder(o)}
                              >
                                <i className="bi bi-trash" /> Cancel Order
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })
            ) : (
              <tr>
                <td colSpan={7}>
                  <div className="empty">
                    <div className="empty-ico">
                      <i className="bi bi-box-seam" />
                    </div>
                    <div
                      style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}
                    >
                      No orders found
                    </div>
                    <div className="fs11 tc3">
                      {search
                        ? "Try a different search term"
                        : "No orders available for your account"}
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}